import { FC } from 'react';

import { usePage } from '@inertiajs/react';
import { format } from 'date-fns';
import {
    Calendar,
    CalendarOff,
    CheckCircle,
    MessageSquare,
    MapPin,
    PackageCheck,
    ShieldCheck,
    Store,
} from 'lucide-react';

import { SellerPrimaryNavigation } from './SellerPrimaryNavigation';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { PublicUser, ShippingMethod } from '../../types';
import {
    formatLocalDateTime,
    formatReviewScore,
    getProductsCountText,
    getReviewsCountText,
    getSalesCountText,
} from '../../utils';

interface Props {
    user: PublicUser;
}

const SHIPPING_METHOD_LABELS: Record<ShippingMethod, string> = {
    nova_poshta: 'Нова пошта',
    ukrposhta: 'Укрпошта',
    pickup: 'Самовивіз',
};

export const ProfileInfo: FC<Props> = ({ user }) => {
    const { url } = usePage();
    const formattedDate = format(new Date(user.created_at), 'dd.MM.yyyy');
    const completedSalesCount = user.completed_sales_count ?? 0;
    const reviewsCount = user.reviews_count ?? 0;
    const averageOverallRatingScore = user.average_overall_rating_score ?? 0;
    const offersCount = user.offers_count ?? 0;
    const sellerPaused = Boolean(user.seller_paused);
    const shippingMethods = (user.shipping_methods ?? [])
        .map((method) => SHIPPING_METHOD_LABELS[method])
        .filter(Boolean);

    const metrics = [
        {
            icon: Store,
            label: 'Каталог продавця',
            value:
                offersCount > 0
                    ? getProductsCountText(offersCount)
                    : 'Поки без товарів',
        },
        {
            icon: CheckCircle,
            label: 'Завершені продажі',
            value: getSalesCountText(completedSalesCount),
        },
        ...(reviewsCount > 0
            ? [
                  {
                      icon: MessageSquare,
                      label: 'Усього відгуків',
                      value: getReviewsCountText(reviewsCount),
                  },
                  {
                      icon: ShieldCheck,
                      label: 'Середня оцінка',
                      value: `${formatReviewScore(averageOverallRatingScore)}/5`,
                  },
              ]
            : []),
    ];

    return (
        <section
            id="seller-about"
            className="-mt-5 pt-2 pb-2 sm:-mt-6 sm:pt-3 sm:pb-3"
            data-testid="seller-profile-hero"
        >
            <div className="container px-4 sm:px-6 lg:px-8">
                <Card className="overflow-hidden rounded-2xl border-border/70 bg-gradient-to-br from-primary/6 via-background to-background shadow-sm">
                    <CardContent className="p-6 sm:p-8">
                        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] xl:items-start">
                            <div className="space-y-5">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                                    <Avatar className="h-24 w-24 border-4 border-background shadow-lg md:h-28 md:w-28">
                                        <AvatarImage
                                            src={user.avatar}
                                            alt={user.nickname}
                                        />
                                        <AvatarFallback className="text-lg md:text-2xl">
                                            {user.nickname
                                                .split(' ')
                                                .map((name) => name[0])
                                                .join('')}
                                        </AvatarFallback>
                                    </Avatar>

                                    <div className="min-w-0 flex-1 space-y-4">
                                        <div className="space-y-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
                                                    {user.nickname}
                                                </h1>

                                                {completedSalesCount > 0 && (
                                                    <Badge
                                                        variant="neutral"
                                                        className="gap-1.5 px-2.5 py-1"
                                                    >
                                                        <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                                        {completedSalesCount}
                                                    </Badge>
                                                )}

                                                {reviewsCount > 0 && (
                                                    <Badge
                                                        variant="outline"
                                                        className="gap-1.5 px-2.5 py-1"
                                                    >
                                                        <MessageSquare className="h-3.5 w-3.5 text-blue-600" />
                                                        {formatReviewScore(
                                                            averageOverallRatingScore,
                                                        )}
                                                        /5
                                                    </Badge>
                                                )}
                                            </div>

                                            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar className="h-4 w-4" />
                                                    Приєднався {formattedDate}
                                                </div>

                                                {user.city && (
                                                    <div
                                                        className="flex items-center gap-1.5"
                                                        data-testid="seller-city"
                                                    >
                                                        <MapPin className="h-4 w-4" />
                                                        {user.city}
                                                    </div>
                                                )}

                                                {shippingMethods.length > 0 && (
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <PackageCheck className="h-4 w-4" />
                                                        {shippingMethods.join(
                                                            ' • ',
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {user.bio && (
                                            <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                                                {user.bio}
                                            </p>
                                        )}

                                        {sellerPaused && (
                                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-500/10 dark:text-amber-100">
                                                <div className="flex items-start gap-2">
                                                    <CalendarOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
                                                    <p>
                                                        Продавець тимчасово не
                                                        приймає замовлення
                                                        {user.seller_paused_until
                                                            ? ` до ${formatLocalDateTime(user.seller_paused_until)}`
                                                            : ''}
                                                        .
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <SellerPrimaryNavigation
                                            currentUrl={url}
                                            sellerNickname={user.nickname}
                                            sellerUrl={user.url_path}
                                            embedded
                                            showReviews={reviewsCount > 0}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid gap-3 sm:grid-cols-2">
                                {metrics.map((metric) => {
                                    const Icon = metric.icon;

                                    return (
                                        <Card
                                            key={metric.label}
                                            className="rounded-2xl border-border/70 bg-background/85 shadow-sm"
                                        >
                                            <CardContent className="flex h-full items-start justify-between gap-4 p-4">
                                                <div className="space-y-1.5">
                                                    <p className="text-sm text-muted-foreground">
                                                        {metric.label}
                                                    </p>
                                                    <p className="text-sm leading-6 font-semibold sm:text-base">
                                                        {metric.value}
                                                    </p>
                                                </div>

                                                <div className="rounded-full bg-primary/10 p-2 text-primary">
                                                    <Icon className="h-4 w-4" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </section>
    );
};
